import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../components/layout/MainLayout';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { getTenders, deleteTender } from '../../services/tenderService';
import toast from 'react-hot-toast';

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-muted text-muted-foreground', icon: 'FileEdit' },
  analyzing: { label: 'Analyzing', color: 'bg-blue-500/15 text-blue-400', icon: 'Loader2' },
  analyzed: { label: 'Analyzed', color: 'bg-success/15 text-success', icon: 'CheckCircle2' },
  submitted: { label: 'Submitted', color: 'bg-purple-500/15 text-purple-400', icon: 'Send' },
  won: { label: 'Won', color: 'bg-emerald-500/15 text-emerald-400', icon: 'Trophy' },
  lost: { label: 'Lost', color: 'bg-error/15 text-error', icon: 'XCircle' },
  cancelled: { label: 'Cancelled', color: 'bg-muted text-muted-foreground', icon: 'Ban' },
};

const CATEGORY_COLORS = {
  IT: 'bg-blue-500/15 text-blue-400',
  Construction: 'bg-orange-500/15 text-orange-400',
  Consulting: 'bg-purple-500/15 text-purple-400',
  Supply: 'bg-teal-500/15 text-teal-400',
  Maintenance: 'bg-yellow-500/15 text-yellow-400',
  Other: 'bg-muted text-muted-foreground',
};

const UploadedTenders = () => {
  const navigate = useNavigate();
  const [tenders, setTenders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    loadTenders();
  }, []);

  const loadTenders = async () => {
    setLoading(true);
    try {
      const data = await getTenders();
      setTenders(data);
    } catch (err) {
      toast.error('Failed to load tenders');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteTender(id);
      setTenders((prev) => prev.filter((t) => t.id !== id));
      toast.success('Tender deleted');
    } catch {
      toast.error('Failed to delete tender');
    } finally {
      setDeleteId(null);
    }
  };

  const filtered = tenders.filter((t) => {
    const q = search.toLowerCase();
    const matchSearch = !q || t?.tenderName?.toLowerCase().includes(q) || t?.clientName?.toLowerCase().includes(q) || t?.tenderNumber?.toLowerCase().includes(q);
    const matchStatus = !statusFilter || t?.status === statusFilter;
    const matchCat = !categoryFilter || t?.tenderCategory === categoryFilter;
    return matchSearch && matchStatus && matchCat;
  });

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
  const formatValue = (v) => v ? `OMR ${Number(v).toLocaleString('en', { minimumFractionDigits: 3 })}` : '—';

  return (
    <MainLayout>
      <div className="space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
              <Icon name="FolderOpen" size={24} className="text-primary" />
              Uploaded Tenders
            </h1>
            <p className="text-muted-foreground text-sm mt-0.5">{tenders.length} tender{tenders.length !== 1 ? 's' : ''} in system</p>
          </div>
          <Button onClick={() => navigate('/tender-analyzer')} className="gap-2">
            <Icon name="Plus" size={16} />
            New Tender
          </Button>
        </div>

        {/* Filters */}
        <div className="bg-card border border-border rounded-2xl p-4 flex flex-wrap gap-3">
          <div className="flex-1 min-w-48 relative">
            <Icon name="Search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search tenders..."
              className="w-full bg-muted/40 border border-border rounded-lg pl-9 pr-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-muted/40 border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary">
            <option value="">All Statuses</option>
            {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}
            className="bg-muted/40 border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary">
            <option value="">All Categories</option>
            {['IT', 'Construction', 'Consulting', 'Supply', 'Maintenance', 'Other'].map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        {/* Table */}
        <div className="bg-card border border-border rounded-2xl overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Icon name="Loader2" size={28} className="animate-spin text-primary" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16">
              <Icon name="FolderOpen" size={40} className="mx-auto text-muted-foreground mb-3" />
              <p className="text-muted-foreground">No tenders found</p>
              <Button variant="outline" onClick={() => navigate('/tender-analyzer')} className="mt-3 gap-2">
                <Icon name="Plus" size={14} />
                Upload First Tender
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    <th className="text-left text-xs font-semibold text-muted-foreground px-4 py-3">Tender</th>
                    <th className="text-left text-xs font-semibold text-muted-foreground px-4 py-3">Client</th>
                    <th className="text-left text-xs font-semibold text-muted-foreground px-4 py-3">Category</th>
                    <th className="text-left text-xs font-semibold text-muted-foreground px-4 py-3">Status</th>
                    <th className="text-left text-xs font-semibold text-muted-foreground px-4 py-3">Compliance</th>
                    <th className="text-left text-xs font-semibold text-muted-foreground px-4 py-3">Closing Date</th>
                    <th className="text-left text-xs font-semibold text-muted-foreground px-4 py-3">Value</th>
                    <th className="text-left text-xs font-semibold text-muted-foreground px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((tender) => {
                    const sc = STATUS_CONFIG[tender?.status] || STATUS_CONFIG.draft;
                    const cc = CATEGORY_COLORS[tender?.tenderCategory] || CATEGORY_COLORS.Other;
                    const compliance = tender?.complianceOverall || 0;
                    const isUrgent = tender?.closingDate && (new Date(tender.closingDate) - new Date()) / (1000 * 60 * 60 * 24) <= 7;
                    return (
                      <tr key={tender.id} className="border-b border-border/50 hover:bg-muted/20 transition-colors">
                        <td className="px-4 py-3">
                          <div>
                            <p className="font-medium text-foreground text-sm">{tender?.tenderName}</p>
                            <p className="text-xs text-muted-foreground">{tender?.tenderNumber || '—'}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-foreground">{tender?.clientName || '—'}</td>
                        <td className="px-4 py-3">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cc}`}>{tender?.tenderCategory}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex items-center gap-1 w-fit ${sc.color}`}>
                            <Icon name={sc.icon} size={11} className={sc.icon === 'Loader2' ? 'animate-spin' : ''} />
                            {sc.label}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {compliance > 0 ? (
                            <div className="flex items-center gap-2">
                              <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
                                <div className={`h-full rounded-full ${compliance >= 90 ? 'bg-success' : compliance >= 75 ? 'bg-warning' : 'bg-error'}`}
                                  style={{ width: `${compliance}%` }} />
                              </div>
                              <span className={`text-xs font-semibold ${compliance >= 90 ? 'text-success' : compliance >= 75 ? 'text-warning' : 'text-error'}`}>
                                {compliance}%
                              </span>
                            </div>
                          ) : <span className="text-xs text-muted-foreground">Not analyzed</span>}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-sm ${isUrgent ? 'text-error font-semibold' : 'text-foreground'}`}>
                            {formatDate(tender?.closingDate)}
                            {isUrgent && <span className="ml-1 text-xs">⚠</span>}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-foreground">{formatValue(tender?.estimatedValue)}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            {tender?.status === 'analyzed' && (
                              <button
                                onClick={() => navigate(`/tender-compliance/${tender.id}`)}
                                className="p-1.5 rounded-lg hover:bg-primary/10 text-primary transition-colors"
                                title="View Compliance Report"
                              >
                                <Icon name="BarChart2" size={15} />
                              </button>
                            )}
                            {deleteId === tender.id ? (
                              <div className="flex items-center gap-1">
                                <button onClick={() => handleDelete(tender.id)} className="text-xs text-error hover:underline">Confirm</button>
                                <button onClick={() => setDeleteId(null)} className="text-xs text-muted-foreground hover:underline">Cancel</button>
                              </div>
                            ) : (
                              <button
                                onClick={() => setDeleteId(tender.id)}
                                className="p-1.5 rounded-lg hover:bg-error/10 text-muted-foreground hover:text-error transition-colors"
                                title="Delete"
                              >
                                <Icon name="Trash2" size={15} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
};

export default UploadedTenders;
